import { useEffect, useState } from 'react'
import { Check, Copy, ExternalLink, Mail, Send, X } from 'lucide-react'
import { api } from '../lib/api'
import { cx } from '../lib/format'
import type { EmailDraft, ViolationCase } from '../lib/types'
import { Callout, Field, Input, Spinner, Textarea } from './ui'

export function EmailDialog({
  violationCase,
  onClose,
  onUpdated,
}: {
  violationCase: ViolationCase
  onClose: () => void
  onUpdated: (updated: ViolationCase) => void
}) {
  const [draft, setDraft] = useState<EmailDraft | null>(null)
  const [smtpEnabled, setSmtpEnabled] = useState(false)
  const [url, setUrl] = useState('')
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [copied, setCopied] = useState<'link' | 'body' | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .emailDraft(violationCase.caseNumber)
      .then((payload) => {
        if (cancelled) return
        setDraft(payload.draft)
        setSmtpEnabled(payload.smtpEnabled)
        setUrl(payload.url)
        setTo(payload.draft.to.join(', '))
        setCc(payload.draft.cc.join(', '))
        setSubject(payload.draft.subject)
        setBody(payload.draft.text)
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : 'Could not build the draft.'))
    return () => {
      cancelled = true
    }
  }, [violationCase.caseNumber])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handler)
      document.body.style.overflow = ''
    }
  }, [onClose])

  const copy = async (value: string, which: 'link' | 'body') => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(which)
      setTimeout(() => setCopied(null), 1800)
    } catch {
      setError('Clipboard access was blocked. Select the text and copy manually.')
    }
  }

  const buildMailtoNow = () => {
    const params = new URLSearchParams()
    if (cc.trim()) params.set('cc', cc)
    params.set('subject', subject)
    params.set('body', body)
    return `mailto:${encodeURIComponent(to)}?${params.toString().replace(/\+/g, '%20')}`
  }

  const sendViaSmtp = async () => {
    setSending(true)
    setError(null)
    try {
      const result = await api.sendEmail(violationCase.caseNumber, { to, cc, subject, text: body })
      if (result.delivered) {
        onUpdated(result.case)
        setSent(true)
      } else {
        setError('SMTP is not configured on the server. Use your own mail app instead.')
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Sending failed.')
    } finally {
      setSending(false)
    }
  }

  const openMailClient = async () => {
    window.location.href = buildMailtoNow()
    try {
      const result = await api.sendEmail(violationCase.caseNumber, {
        to,
        cc,
        subject,
        markSentOnly: true,
      })
      onUpdated(result.case)
      setSent(true)
    } catch {
      /* logging the send is best-effort */
    }
  }

  return (
    <div className="no-print fixed inset-0 z-50 flex animate-fade-in items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:p-8">
      <div className="w-full max-w-3xl animate-slide-up rounded-xl border border-white/[0.09] bg-ink-850 shadow-lift">
        <header className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-altair-500/30 bg-altair-500/10 text-altair-400">
              <Mail size={16} />
            </span>
            <div>
              <h2 className="text-[15px] font-semibold text-white">Send report to HOA management</h2>
              <p className="text-[12px] text-slate-500">
                {violationCase.caseNumber} · {violationCase.hoa?.management}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn-subtle p-2" aria-label="Close">
            <X size={17} />
          </button>
        </header>

        {!draft && !error ? (
          <div className="flex items-center gap-2 px-5 py-10 text-[13px] text-slate-400">
            <Spinner /> Building the draft…
          </div>
        ) : null}

        {draft ? (
          <div className="space-y-4 px-5 py-5">
            <div className="rounded-lg border border-white/[0.08] bg-ink-900/70 p-3">
              <div className="label mb-2">Shareable case link</div>
              <div className="flex flex-wrap items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded bg-black/40 px-2.5 py-2 font-mono text-[12.5px] text-altair-300">
                  {url}
                </code>
                <button type="button" onClick={() => copy(url, 'link')} className="btn-ghost px-2.5 py-2">
                  {copied === 'link' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  {copied === 'link' ? 'Copied' : 'Copy'}
                </button>
                <a href={url} target="_blank" rel="noreferrer" className="btn-ghost px-2.5 py-2">
                  <ExternalLink size={14} />
                  Open
                </a>
              </div>
              <p className="mt-2 text-[11.5px] leading-relaxed text-slate-500">
                This link opens the full report with playable video in any browser — no login, so the
                office manager can review it with the resident on the spot.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="To" hint="Comma-separated.">
                <Input value={to} onChange={(event) => setTo(event.target.value)} placeholder="manager@seabreezemgmt.com" />
              </Field>
              <Field label="CC">
                <Input value={cc} onChange={(event) => setCc(event.target.value)} placeholder="board@…" />
              </Field>
            </div>

            <Field label="Subject">
              <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
            </Field>

            <Field
              label="Message"
              hint="Edit freely — the case link stays at the top and bottom of the message."
            >
              <Textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                className="min-h-[280px] font-mono text-[12.5px] leading-relaxed"
              />
            </Field>

            {error ? <Callout tone="danger">{error}</Callout> : null}
            {sent ? (
              <Callout tone="success" title="Logged on the case timeline">
                The send was recorded. You can close this window.
              </Callout>
            ) : null}

            {!smtpEnabled ? (
              <Callout tone="info" title="No mail server configured">
                The portal will hand this off to your own mail app (Mail, Gmail, Outlook) with
                everything prefilled. To send directly from the portal instead, add SMTP settings to{' '}
                <code className="rounded bg-black/40 px-1">.env</code>.
              </Callout>
            ) : null}
          </div>
        ) : null}

        {error && !draft ? (
          <div className="px-5 py-5">
            <Callout tone="danger">{error}</Callout>
          </div>
        ) : null}

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.07] px-5 py-4">
          <button
            type="button"
            onClick={() => copy(body, 'body')}
            className={cx('btn-subtle', !draft && 'pointer-events-none opacity-50')}
          >
            {copied === 'body' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
            Copy message text
          </button>

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">
              Close
            </button>
            <button
              type="button"
              onClick={openMailClient}
              className={cx(smtpEnabled ? 'btn-ghost' : 'btn-primary', !draft && 'pointer-events-none opacity-50')}
            >
              <Mail size={15} />
              Open in mail app
            </button>
            {smtpEnabled ? (
              <button
                type="button"
                onClick={sendViaSmtp}
                disabled={sending || !draft}
                className="btn-primary"
              >
                {sending ? <Spinner /> : <Send size={15} />}
                {sending ? 'Sending…' : 'Send now'}
              </button>
            ) : null}
          </div>
        </footer>
      </div>
    </div>
  )
}
